import React, { useState } from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import ProcessStep from './ProcessStep';
import InvoiceInfoModal from './InvoiceInfoModal';
import ReceiptButton from './ReceiptButton';
import { getProcessSteps, getCurrentStepIndex } from '../../util/stepConfig';
import { getStepTimestamp as getTimestamp } from '../../util/processStepHelper';
import { PrimaryButton, Button, Modal, NeedHelpInfo } from '../../components';
import { EnquiryForm } from '../../forms';
import { ensureListing, ensureUser } from '../../util/data';
import { customOfferDataFromTransaction } from '../../util/customOffer';
import { offerTotals, resolutionForOffer, offerVatKnown } from '../../util/vat';
import { receiptKindFor, RECEIPT_KIND } from '../../util/receipts/eligibility';
import {
  TRANSITION_MAKE_CUSTOM_OFFER,
  TRANSITION_REQUEST_PAYMENT_AFTER_ENQUIRY,
  TRANSITION_CONFIRM_PAYMENT,
  TRANSITION_ACCEPT_CUSTOM_PRO_OFFER,
  TRANSITION_RELEASE_FUNDS,
  TRANSITION_REVIEW_1_BY_PROVIDER,
  TRANSITION_REVIEW_2_BY_PROVIDER,
  TRANSITION_REVIEW_1_BY_CUSTOMER,
  TRANSITION_REVIEW_2_BY_CUSTOMER,
  TRANSITION_JOB_DONE_PRO,
  TRANSITION_JOB_ACCEPTED,
  TRANSITION_CANCEL_PAYMENT,
  TRANSITION_ACCEPT_CUSTOM_OFFER_INVOICE,
  TRANSITION_ACCEPT_JOB_INVOICE,
  TRANSITION_JOB_DONE_ACCEPT_INVOICE,
  txUsesInvoicePath,
  txIsLegacyProProcess,
  txIsInFirstReviewBy,
} from '../../util/transaction';

import css from './TransactionPanel.css';

// The customer's "+ alv" on the offer row: never while the offer is pending
// (its VAT is settled on the confirm step or the checkout, and the row's
// amount is the VAT-free total of work and fee), afterwards only when the
// customer's supply carries VAT.
export const customerOfferVatSuffix = ({ offerPending, customerSupply }) =>
  !offerPending && !!customerSupply && customerSupply.rate > 0;

// The step a downloadable document hangs under (downloadable receipts spec §7):
// the paid offer for the customer, the released or accepted work for the provider.
export const receiptStepIdFor = (kind, usesInvoicePath) => {
  if (kind === RECEIPT_KIND.CUSTOMER_RECEIPT) return 'offer-sent';
  if (kind === RECEIPT_KIND.PROVIDER_RECEIPT) return 'payment-released';
  if (kind === RECEIPT_KIND.PROVIDER_BILLING_BASIS) {
    return usesInvoicePath ? 'job-accepted' : 'payment-released';
  }
  return null;
};

const ProcessStepsCard = ({
  transaction,
  transactionRole,
  vatPreview = null,
  intl,
  stepActions,
  actionInProgress,
  isProOrInvoiceCustomer,
  onManageDisableScrolling,
  listing,
  provider,
  listingTitle,
  isListingClosed,
  onSubmitEnquiryCustomer,
  sendEnquiryInProgress,
  sendEnquiryErrorListing,
  openCustomOfferModalButton,
  onOpenOfferModal,
  onDownloadReceipt,
  downloadReceiptInProgressId,
  downloadReceiptFailedId,
}) => {
  // Extract transaction data
  const {
    processName,
    lastTransition,
    protectedData = {},
    payoutTotal,
    lineItems = [],
  } = transaction.attributes;
  const transitions = transaction.attributes.transitions || [];
  const isCustomer = transactionRole === 'customer';
  const usesInvoicePath = txUsesInvoicePath(transaction);
  const isLegacyPro = txIsLegacyProProcess(transaction);
  const providerAccountRole = provider?.attributes?.profile?.publicData?.account_role;

  // This party's download, under the step that made it due.
  const receiptKind = receiptKindFor(transaction, transactionRole, providerAccountRole);
  const receiptStepId = receiptStepIdFor(receiptKind, usesInvoicePath);
  const txUuid = transaction.id && transaction.id.uuid;
  const receiptButton =
    receiptKind && onDownloadReceipt ? (
      <ReceiptButton
        kind={receiptKind}
        inProgress={!!txUuid && downloadReceiptInProgressId === txUuid}
        failed={!!txUuid && downloadReceiptFailedId === txUuid}
        onDownload={() =>
          onDownloadReceipt({ transactionId: txUuid, kind: receiptKind, locale: intl.locale })
        }
      />
    ) : null;

  // Get process steps and current position
  const processSteps = getProcessSteps(usesInvoicePath, isLegacyPro);
  const currentStepIndex = getCurrentStepIndex(transaction, processSteps);

  // Custom offer state
  const customOffer = {
    amount: protectedData.customOfferAmount,
    description: protectedData.customOfferDescription || '',
    startDate: protectedData.customOfferStartDate,
    deliveryDate: protectedData.customOfferDeliveryDate,
    exists: protectedData.customOfferAmount !== undefined,
    accepted:
      lastTransition === TRANSITION_CONFIRM_PAYMENT ||
      lastTransition === TRANSITION_ACCEPT_CUSTOM_PRO_OFFER ||
      lastTransition === TRANSITION_ACCEPT_CUSTOM_OFFER_INVOICE,
  };

  // The display rule: a pending offer previews, anything accepted prints its
  // line items (customer service fee spec §1).
  const offerPending = customOffer.exists && !customOffer.accepted;
  const stepResolution = resolutionForOffer(transaction, vatPreview);
  const vatTotals = offerTotals({
    lineItems,
    isPending: offerPending,
    resolution: stepResolution,
    customerPrice: protectedData.customOfferAmount || 0,
  });
  // A pending offer's rate is known only from an ok preview; until then the
  // row names the VAT-exclusive price without a "+ VAT" claim.
  const vatKnown = offerVatKnown(vatPreview, offerPending);
  const stepSupplies = stepResolution.supplies || {};
  const customerSupply = stepSupplies.core || stepSupplies.customerSale;
  const providerSupply = stepSupplies.core || stepSupplies.providerSale;
  const showCustomerVatSuffix = customerOfferVatSuffix({ offerPending, customerSupply });
  const showProviderVatSuffix = vatKnown && !!providerSupply && providerSupply.rate > 0;

  // Modal state for new enquiry
  const [newEnquiryModalOpen, setNewEnquiryModalOpen] = useState(false);
  // Modal state for invoice info
  const [invoiceInfoModalOpen, setInvoiceInfoModalOpen] = useState(false);

  // Ensure listing and provider
  const currentListing = ensureListing(listing);
  const currentProvider = ensureUser(provider);

  // Check if we should show the new enquiry button
  const reviewTransitions = [
    TRANSITION_REVIEW_1_BY_PROVIDER,
    TRANSITION_REVIEW_2_BY_PROVIDER,
    TRANSITION_REVIEW_1_BY_CUSTOMER,
    TRANSITION_REVIEW_2_BY_CUSTOMER,
  ];
  const canStartNewEnquiry =
    isCustomer &&
    !isListingClosed &&
    (lastTransition === TRANSITION_RELEASE_FUNDS ||
      lastTransition === TRANSITION_JOB_ACCEPTED ||
      lastTransition === TRANSITION_ACCEPT_JOB_INVOICE ||
      lastTransition === TRANSITION_JOB_DONE_ACCEPT_INVOICE ||
      reviewTransitions.includes(lastTransition));

  // Check if we should show the new enquiry notice for provider
  const canShowProviderNewEnquiryNotice =
    !isCustomer &&
    !isListingClosed &&
    (lastTransition === TRANSITION_RELEASE_FUNDS ||
      lastTransition === TRANSITION_JOB_ACCEPTED ||
      lastTransition === TRANSITION_ACCEPT_JOB_INVOICE ||
      lastTransition === TRANSITION_JOB_DONE_ACCEPT_INVOICE ||
      reviewTransitions.includes(lastTransition));

  // After cancel-payment this transaction can no longer be used for payment: a fresh
  // request-payment-after-enquiry on the same tx is rejected by Sharetribe because the
  // payin record stays in 'released' state (see docs/payment-retry-flow-fix.md). Tell
  // the user explicitly and offer the customer a path forward via a brand new enquiry.
  const isPaymentCanceled = lastTransition === TRANSITION_CANCEL_PAYMENT;
  const showPaymentCanceledForCustomer = isPaymentCanceled && isCustomer && !isListingClosed;
  const showPaymentCanceledForProvider = isPaymentCanceled && !isCustomer;

  // Check if we should show "Show offer" button for provider on job-accepted/payment-released step
  const isLightProcess = processName === 'flex-light-process';
  const shouldShowOfferButtonForProvider =
    !isCustomer &&
    providerAccountRole === 'light-entrepreneur' &&
    ((usesInvoicePath &&
      ([
        TRANSITION_JOB_ACCEPTED,
        TRANSITION_ACCEPT_JOB_INVOICE,
        TRANSITION_JOB_DONE_ACCEPT_INVOICE,
      ].includes(lastTransition) ||
        reviewTransitions.includes(lastTransition))) ||
      (isLightProcess &&
        (lastTransition === TRANSITION_RELEASE_FUNDS ||
          reviewTransitions.includes(lastTransition))));

  // Helper functions
  const getStepStatus = stepIndex => {
    if (stepIndex < currentStepIndex) return 'completed';
    if (stepIndex === currentStepIndex) return 'current';
    return 'pending';
  };

  const getStepTimestamp = (step, status, stepIndex) => {
    // Special handling for reviews step - show when it was completed (second review)
    // not when it started (funds released)
    if ((step.id === 'reviews' || step.id === 'job-accepted') && status === 'completed') {
      // Find the second review transition (REVIEW_2)
      const secondReviewTransition = transitions.find(
        t =>
          t.transition === TRANSITION_REVIEW_2_BY_CUSTOMER ||
          t.transition === TRANSITION_REVIEW_2_BY_PROVIDER
      );
      if (secondReviewTransition) {
        return secondReviewTransition.createdAt;
      }
    }

    // For all other steps, use the centralized helper utility
    return getTimestamp(step, status, stepIndex, processSteps, transitions);
  };

  const getInstructionText = (step, isCurrent) => {
    if (!isCurrent || !step.instruction) return null;

    // Special handling for reviews step - this is now the LAST step where reviews happen
    if (step.id === 'reviews') {
      const userHasLeftReview = txIsInFirstReviewBy(transaction, isCustomer);
      if (userHasLeftReview) {
        // User has left their review, show waiting message
        return 'TransactionSteps.waitingForOtherPartyReview';
      }
      // User hasn't left review yet, show normal instruction
      if (step.instruction.both) return step.instruction.both;
      return isCustomer ? step.instruction.customer : step.instruction.provider;
    }

    // For job-accepted step (pro process), check provider's account_role for payout instructions
    if (step.id === 'job-accepted' && !isCustomer) {
      const providerAccountRole = provider?.attributes?.profile?.publicData?.account_role;
      if (providerAccountRole === 'freelancer') {
        return 'TransactionSteps.providerAutomaticPayoutInstruction';
      }
      return step.instruction.provider;
    }

    // For payment-released step (default/light process), check provider's account_role
    if (step.id === 'payment-released' && !isCustomer) {
      const providerAccountRole = provider?.attributes?.profile?.publicData?.account_role;
      if (providerAccountRole === 'freelancer') {
        return 'TransactionSteps.providerAutomaticPayoutInstruction';
      }
      return step.instruction.provider;
    }

    if (step.instruction.both) return step.instruction.both;
    return isCustomer ? step.instruction.customer : step.instruction.provider;
  };

  const getActionButton = (step, isCurrent) => {
    if (!isCurrent || !step.action) return null;
    const roleAction =
      (isCustomer ? step.action.customer : step.action.provider) || step.action.both;
    if (!roleAction || !stepActions[roleAction.handler]) return null;

    // For review actions, check if user has already written their review
    // Reviews are now ONLY on the 'reviews' step (the last step)
    if (roleAction.handler === 'onOpenReviewModal') {
      // Check if this user has already left their review
      const userHasLeftReview = txIsInFirstReviewBy(transaction, isCustomer);
      if (userHasLeftReview) {
        return null; // Don't show review button if user already reviewed
      }
    }

    // Check if this is the work completion button for provider after payment confirmation
    const isProviderWorkCompletionButton =
      !isCustomer &&
      [
        TRANSITION_CONFIRM_PAYMENT,
        TRANSITION_ACCEPT_CUSTOM_PRO_OFFER,
        TRANSITION_ACCEPT_CUSTOM_OFFER_INVOICE,
      ].includes(lastTransition) &&
      ['onJobReady', 'onJobReadyPro', 'onJobDoneInvoicePath'].includes(roleAction.handler);

    return (
      <PrimaryButton
        className={isProviderWorkCompletionButton ? undefined : css.openCustomOfferModalButton}
        onClick={stepActions[roleAction.handler]}
        inProgress={actionInProgress}
        disabled={actionInProgress}
      >
        <FormattedMessage id={roleAction.label} />
      </PrimaryButton>
    );
  };

  return (
    <div className={css.processStepsCard}>
      {/* <div className={css.processStepsHeader}>
        <h3 className={css.processStepsTitle}>
          <FormattedMessage id="ProcessSteps.header" />
        </h3>
      </div> */}

      <div className={css.processStepsList}>
        {processSteps.map((step, index) => {
          const status = getStepStatus(index);
          const isCurrent = status === 'current';
          const isOfferStep = step.id === 'offer-sent';
          const isConversationStep = step.id === 'conversation-started';

          // Determine custom step title for offer step
          let customStepTitle = null;
          if (isOfferStep && isCurrent) {
            if (customOffer.exists && !customOffer.accepted) {
              customStepTitle = isCustomer
                ? 'TransactionSteps.offerReceivedWaitingAcceptance'
                : 'TransactionSteps.offerSentWaitingAcceptance';
            } else if (!customOffer.exists && !isCustomer) {
              customStepTitle = 'CustomOfferForm.makeAnOffer';
            }
          }

          // Build custom offer data if relevant
          const shouldShowOffer =
            isOfferStep &&
            customOffer.exists &&
            ((isCurrent && !customOffer.accepted) ||
              (status === 'completed' && customOffer.accepted));

          const customOfferData = shouldShowOffer
            ? customOfferDataFromTransaction(transaction, isCustomer)
            : null;

          // Check if this is a pending offer waiting for customer acceptance
          const isPendingOffer =
            isOfferStep && isCurrent && customOffer.exists && !customOffer.accepted && isCustomer;

          // Check if this is the job-accepted or payment-released step where we should show offer button
          const isJobAcceptedStep = step.id === 'job-accepted';
          const isPaymentReleasedStep = step.id === 'payment-released';
          const showOfferButton =
            shouldShowOfferButtonForProvider &&
            ((isJobAcceptedStep && usesInvoicePath) || (isPaymentReleasedStep && isLightProcess));

          return (
            <ProcessStep
              key={step.id}
              step={step}
              status={status}
              timestamp={getStepTimestamp(step, status, index)}
              intl={intl}
              isLast={index === processSteps.length - 1}
              actionButton={getActionButton(step, isCurrent)}
              instructionText={getInstructionText(step, isCurrent)}
              showInstruction={isCurrent}
              customOfferInfo={
                isCurrent && isConversationStep && !isCustomer && !customOffer.exists
              }
              customOfferData={customOfferData}
              isCustomer={isCustomer}
              vatTotals={vatTotals}
              showCustomerVatSuffix={showCustomerVatSuffix}
              showProviderVatSuffix={showProviderVatSuffix}
              onOpenOfferModal={onOpenOfferModal}
              customStepTitle={customStepTitle}
              isPendingOffer={isPendingOffer}
              openCustomOfferModalButton={
                isCurrent && isConversationStep && !customOffer.exists && !isCustomer
                  ? openCustomOfferModalButton
                  : null
              }
              showOfferButton={showOfferButton}
              onOpenInvoiceModal={() => setInvoiceInfoModalOpen(true)}
              receiptButton={step.id === receiptStepId ? receiptButton : null}
            />
          );
        })}
      </div>

      {canStartNewEnquiry && (
        <div className={css.newEnquiryButton}>
          <div className={css.newEnquiryTitle}>
            <FormattedMessage
              id="TransactionPanel.newEnquiryTitle"
              values={{ providerName: currentProvider.attributes?.profile?.displayName }}
            />
          </div>
          <Button
            className={css.openCustomOfferModalButton}
            onClick={() => setNewEnquiryModalOpen(true)}
          >
            <FormattedMessage id="TransactionPanel.newEnquiryButton" />
          </Button>
        </div>
      )}

      {canShowProviderNewEnquiryNotice && (
        <div className={css.newEnquiryButton}>
          <div className={css.newEnquiryTitle}>
            <FormattedMessage id="TransactionPanel.providerNewEnquiryNotice" />
          </div>
        </div>
      )}

      {showPaymentCanceledForCustomer && (
        <div className={css.newEnquiryButton}>
          <div className={css.newEnquiryTitle}>
            <FormattedMessage
              id="TransactionPanel.paymentCanceledCustomerNotice"
              values={{ providerName: currentProvider.attributes?.profile?.displayName }}
            />
          </div>
          <Button
            className={css.openCustomOfferModalButton}
            onClick={() => setNewEnquiryModalOpen(true)}
          >
            <FormattedMessage id="TransactionPanel.newEnquiryButton" />
          </Button>
        </div>
      )}

      {showPaymentCanceledForProvider && (
        <div className={css.newEnquiryButton}>
          <div className={css.newEnquiryTitle}>
            <FormattedMessage id="TransactionPanel.paymentCanceledProviderNotice" />
          </div>
        </div>
      )}

      <Modal
        id="ProcessStepsCard.newEnquiry"
        contentClassName={css.enquiryModalContent}
        isOpen={newEnquiryModalOpen}
        onClose={() => setNewEnquiryModalOpen(false)}
        containerClassName={css.enquiryModalContainer}
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.newEnquirySelectContainer}>
          <div className={css.newEnquiryNB}>
            <FormattedMessage id="TransactionPanel.newEnquiryNB" />
          </div>
          <EnquiryForm
            className={css.enquiryForm}
            listingTitle={listingTitle}
            authorDisplayName={currentProvider.attributes?.profile?.displayName}
            author={currentProvider}
            sendEnquiryError={sendEnquiryErrorListing}
            onSubmit={onSubmitEnquiryCustomer}
            inProgress={sendEnquiryInProgress}
          />
        </div>
      </Modal>

      <InvoiceInfoModal
        isOpen={invoiceInfoModalOpen}
        onClose={() => setInvoiceInfoModalOpen(false)}
        onManageDisableScrolling={onManageDisableScrolling}
        customOfferData={{
          amount: customOffer.amount,
          payoutTotal: payoutTotal,
          lineItems: lineItems,
        }}
        intl={intl}
        customerName={transaction.customer?.attributes?.profile?.displayName}
        transactionId={transaction.id?.uuid}
        providerNet={vatTotals.providerNet}
      />

      <NeedHelpInfo arcticleId="7044544" />
    </div>
  );
};

export default ProcessStepsCard;
